import { formatQuantity } from '@ztechapps/common.utils'
import type { CurrencyRate } from '@ztechapps/common.contract'

/**
 * Decimal precision used when a `CurrencyRate` supplies none.
 */
const DEFAULT_CREDIT_DECIMALS = 2

/**
 * Display format for a credit amount, sourced from the account currency's own `CurrencyRate`
 * (`symbol`, `decimals`) rather than a hardcoded currency.
 */
export type CreditAmountFormat = Pick<CurrencyRate, 'symbol' | 'decimals'>

/**
 * Renders a credit amount for display. 1 credit is one unit of the account's own currency —
 * not USD — so the caller supplies `symbol`/`decimals` from that currency's `CurrencyRate`
 * rather than assuming a fixed currency. Thousands separators and decimal precision come from
 * `formatQuantity`, which also treats an absent amount as zero. The sign is emitted before the
 * symbol (e.g. `-₱5.50`, matching `formatMoney`), not between the symbol and the digits.
 *
 * Pure function — no React, no Apollo, no I/O — safe to import from both web and node code.
 * quickscore-mobile formats credit balances its own way and does not import this.
 */
export function formatCreditAmount(amount?: number, format?: CreditAmountFormat): string {
  const decimals = format?.decimals ?? DEFAULT_CREDIT_DECIMALS
  const sign = (amount ?? 0) < 0 ? '-' : ''
  return `${sign}${format?.symbol ?? ''}${formatQuantity(Math.abs(amount ?? 0), decimals)}`
}
